import React, { useMemo } from "react";

import { useSavoieDemo, SAVOIE_DEMO_STATES } from "@/lib/useSavoieDemo";
import ProvenanceBadges from "@/components/provenance/ProvenanceBadges";
import OpportunityCard from "@/components/pilot/OpportunityCard";
import RecommendationCard from "@/components/pilot/RecommendationCard";
import CampaignSimulationCard from "@/components/pilot/CampaignSimulationCard";
import CampaignOutcomesCard from "@/components/pilot/CampaignOutcomesCard";
import PilotScenarioSummaryCard from "@/components/pilot/PilotScenarioSummaryCard";
import { selectGrowthOpportunity } from "@/lib/savoieOpportunity";
import { buildGrowthRecommendation } from "@/lib/savoieRecommendation";
import { buildSimulatedCampaign } from "@/lib/savoieCampaignSimulation";
import { buildSimulatedCampaignOutcomes } from "@/lib/savoieCampaignOutcomes";
import { buildPilotScenarioSummary } from "@/lib/savoiePilotSummary";
import {
  MISSING,
  latestPeriod,
  periodLabel,
  arrivalsByMarket,
  growthByMarket,
  concentration,
  averageStay,
  averageSpendPerVisitor,
} from "@/lib/savoieKpis";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

const PILOT_TITLE = "Destination Alpine Démo — cas fictif Savoie Mont-Blanc";

const nf = new Intl.NumberFormat("fr-FR");
const fmtInt = (v) => (typeof v === "number" ? nf.format(Math.round(v)) : MISSING);
const fmtPct = (v) => (typeof v === "number" ? `${v.toFixed(1)} %` : MISSING);
const fmtStay = (v) => (typeof v === "number" ? `${v.toFixed(1)} nuits` : MISSING);
const fmtMoney = (v, c) =>
  typeof v === "number" ? `${nf.format(Math.round(v))} ${c || ""}`.trim() : MISSING;

function Card({ title, children, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testid}>
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
      {children}
    </div>
  );
}

function StateNotice({ title, message, testid }) {
  return (
    <div className="mx-auto max-w-2xl rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm"
         data-testid={testid}>
      <h2 className="font-heading text-xl font-bold text-slate-800">{title}</h2>
      <p className="mt-2 text-sm text-slate-600">{message}</p>
    </div>
  );
}

export default function PilotDemoSavoie() {
  const { state, data } = useSavoieDemo();

  // Call the pure growth engine ONLY when the hook is ready (never with absent data).
  const opportunity = useMemo(
    () =>
      state === SAVOIE_DEMO_STATES.READY && data ? selectGrowthOpportunity(data) : null,
    [state, data]
  );

  // Derive the SINGLE deterministic recommendation ONLY from a detected
  // opportunity (pure 3D.3-B engine; no second selection, no fallback).
  const recommendation = useMemo(
    () =>
      opportunity && opportunity.status === "detected"
        ? buildGrowthRecommendation(opportunity)
        : null,
    [opportunity]
  );

  // Derive the SINGLE strictly SIMULATED campaign ONLY from a recommended
  // recommendation (pure 3D.4-B engine; no fallback, no default scenario).
  const campaign = useMemo(
    () =>
      recommendation && recommendation.status === "recommended"
        ? buildSimulatedCampaign(recommendation)
        : null,
    [recommendation]
  );

  // Derive the SEPARATE, strictly SIMULATED / non-observed / non-predictive
  // scenario OUTCOMES ONLY from a simulated campaign (pure 3D.5-B engine; no
  // fallback, no parallel analytical chain, no dataset re-read). Hook is always
  // called in the same order (never behind a conditional return).
  const campaignOutcomes = useMemo(
    () =>
      campaign && campaign.status === "simulated"
        ? buildSimulatedCampaignOutcomes(campaign)
        : null,
    [campaign]
  );

  // Consolidate the FOUR canonical outputs into a single read-only summary
  // (pure 3D.6-B engine). Unconditional Hook (never behind a conditional
  // return); it re-exposes existing values only, never rebuilds the chain.
  const pilotScenarioSummary = useMemo(
    () =>
      buildPilotScenarioSummary({
        opportunityResult: opportunity,
        recommendationResult: recommendation,
        campaignResult: campaign,
        outcomesResult: campaignOutcomes,
      }),
    [opportunity, recommendation, campaign, campaignOutcomes]
  );

  if (state === SAVOIE_DEMO_STATES.DISABLED) {
    return (
      <StateNotice
        testid="pilot-state-disabled"
        title="Démonstration désactivée"
        message="Cette démonstration synthétique n'est pas activée dans cet environnement."
      />
    );
  }
  if (state === SAVOIE_DEMO_STATES.LOADING) {
    return (
      <StateNotice testid="pilot-state-loading" title="Chargement…"
        message="Récupération des données de démonstration." />
    );
  }
  if (state === SAVOIE_DEMO_STATES.UNAVAILABLE) {
    return (
      <StateNotice testid="pilot-state-unavailable" title="Démonstration indisponible"
        message="Le jeu de données de démonstration n'est pas disponible actuellement." />
    );
  }
  if (state === SAVOIE_DEMO_STATES.INVALID_CONTRACT) {
    return (
      <StateNotice testid="pilot-state-invalid" title="Données non conformes"
        message="Les données reçues ne respectent pas le contrat attendu. Aucun indicateur n'est affiché." />
    );
  }
  if (state !== SAVOIE_DEMO_STATES.READY || !data) {
    return (
      <StateNotice testid="pilot-state-error" title="Erreur"
        message="Une erreur est survenue lors du chargement des données de démonstration." />
    );
  }

  // READY: compute KPIs purely from the received records.
  const records = data.records;
  const period = latestPeriod(records);
  const arrivals = arrivalsByMarket(records, period);
  const growth = growthByMarket(records, period);
  const conc = concentration(records, period);
  const stay = averageStay(records, period);
  const spend = averageSpendPerVisitor(records, period);
  const growthByKey = Object.fromEntries(growth.map((g) => [g.market, g.growthPct]));

  return (
    <div className="space-y-6" data-testid="pilot-ready">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"
              data-testid="pilot-title">{PILOT_TITLE}</h1>
          <ProvenanceBadges meta={data.meta} />
        </div>
        <p className="text-sm text-slate-600" data-testid="pilot-period">
          Période analysée : <span className="font-semibold">{periodLabel(period)}</span>
        </p>
        <p className="max-w-3xl text-xs text-slate-500" data-testid="pilot-disclaimer">
          {data.disclaimer}
        </p>
      </header>

      <section aria-label="Opportunité métier" data-testid="opportunity-section">
        {opportunity && opportunity.status === "detected" ? (
          <OpportunityCard opportunity={opportunity} />
        ) : (
          <p
            className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm"
            data-testid="opportunity-none"
          >
            Aucune opportunité ne satisfait les critères du pilote pour cette période.
          </p>
        )}
      </section>

      <section aria-label="Recommandation marketing déterministe" data-testid="recommendation-section">
        <RecommendationCard recommendationResult={recommendation} />
      </section>

      <section aria-label="Campagne simulée de démonstration" data-testid="campaign-simulation-section">
        <CampaignSimulationCard campaignResult={campaign} />
      </section>

      <section aria-label="Résultats simulés du scénario" data-testid="campaign-outcomes-section">
        {campaignOutcomes && campaignOutcomes.status === "scenario" ? (
          <CampaignOutcomesCard outcomesResult={campaignOutcomes} />
        ) : null}
      </section>

      {pilotScenarioSummary && pilotScenarioSummary.status === "ready" ? (
        <section aria-label="Synthèse du scénario pilote" data-testid="pilot-scenario-summary-section">
          <PilotScenarioSummaryCard summaryResult={pilotScenarioSummary} />
        </section>
      ) : null}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card title="Concentration des marchés" testid="kpi-concentration">
          {conc.sharePct === MISSING ? (
            <p className="text-slate-500">{MISSING}</p>
          ) : (
            <p><span className="text-2xl font-bold">{fmtPct(conc.sharePct)}</span>
              <span className="ml-2 text-sm text-slate-500">1er marché : {conc.market}</span></p>
          )}
        </Card>
        <Card title="Durée moyenne de séjour" testid="kpi-average-stay">
          <p className="text-2xl font-bold">{fmtStay(stay.nights)}</p>
        </Card>
        <Card title="Dépense moyenne / visiteur" testid="kpi-average-spend">
          <p className="text-2xl font-bold">{fmtMoney(spend.value, spend.currency)}</p>
        </Card>
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Arrivées visiteurs par marché" testid="kpi-arrivals">
          <SelectableTable rows={arrivals} rowId={(row)=>row.market} filename="pilotdemosavoie.csv" ariaLabel="Tableau — sélection et défilement">
            <table className="w-full text-sm min-w-[720px]" data-testid="arrivals-table">
            <thead><tr className="text-left text-slate-500"><th className="px-2 py-2"><SelectAllHeaderCheckbox /></th>
              <th className="py-1">Marché</th><th className="py-1 text-right">Arrivées (visiteurs)</th>
            </tr></thead>
            <tbody>
              {arrivals.map((row) => (
                <tr key={row.market} className="border-t border-slate-100"><td className="px-2 py-2"><RowCheckbox id={row.market} /></td>
                  <td className="py-1">{row.market}</td>
                  <td className="py-1 text-right">{fmtInt(row.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </SelectableTable>
        </Card>
        <Card title="Croissance des arrivées par marché" testid="kpi-growth">
          <SelectableTable rows={arrivals} rowId={(row)=>row.market} filename="pilotdemosavoie.csv" ariaLabel="Tableau — sélection et défilement">
            <table className="w-full text-sm min-w-[720px]" data-testid="growth-table">
            <thead><tr className="text-left text-slate-500"><th className="px-2 py-2"><SelectAllHeaderCheckbox /></th>
              <th className="py-1">Marché</th><th className="py-1 text-right">Croissance (N vs N-1)</th>
            </tr></thead>
            <tbody>
              {arrivals.map((row) => (
                <tr key={row.market} className="border-t border-slate-100"><td className="px-2 py-2"><RowCheckbox id={row.market} /></td>
                  <td className="py-1">{row.market}</td>
                  <td className="py-1 text-right">{fmtPct(growthByKey[row.market])}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </SelectableTable>
        </Card>
      </section>
    </div>
  );
}
